import { observable } from '@legendapp/state';
import { useSelector } from '@legendapp/state/react';
import { globalState } from '@/state/globalState';
import { useState, useEffect, useCallback, useRef } from 'react';
import { databaseService } from '../services/databaseService';
import { realtimeTrigger } from '../state/realtime';
import APP_CONSTANTS from '@/config/constants';
import { filterChecklistsWithReadySnapshots } from '@/services/checklist/ChecklistSnapshotVisibilityService';

// Cada leitura pertence ao escopo e à geração que a iniciaram.
function useChecklistReadGuard(queryKey: string, empresaId: string) {
  const identity = useSelector(() => JSON.stringify([
    globalState.user.user.get()?.id,
    globalState.user.profile.get()?.user_type,
    globalState.user.profile.get()?.id,
    globalState.user.profile.get()?.empresa_id,
  ]));
  const [userId, , profileId, profileCompany] = JSON.parse(identity);
  const coherent = Boolean(userId && userId === profileId && empresaId === profileCompany);
  const key = `${identity}:${queryKey}`;
  const current = useRef({ key, generation: 0 });
  if (current.current.key !== key) {
    current.current = { key, generation: current.current.generation + 1 };
  }
  useEffect(() => () => { current.current.generation++; }, [key]);
  const beginRead = useCallback(() => {
    if (!coherent || current.current.key !== key) return () => false;
    const generation = ++current.current.generation;
    return () => current.current.key === key && current.current.generation === generation;
  }, [key, coherent]);
  return { beginRead, identity, coherent };
}

const PAGE_SIZE = 50;
type ChecklistStatus = 'in_progress' | 'done';

// Caches leves em memória para evitar leituras repetidas do banco local
const templateNameCache: Record<string, string> = {};
const userNameCache: Record<string, string> = {};

function clearChecklistMetadataCaches() {
  Object.keys(templateNameCache).forEach((key) => {
    delete templateNameCache[key];
  });
  Object.keys(userNameCache).forEach((key) => {
    delete userNameCache[key];
  });
}

// Interface da linha base retornada pelo banco local
interface RawChecklistItem {
  id: string;
  empresa_id?: string | null;
  data_criacao: number;
  status: ChecklistStatus;
  data_conclusao?: number;
  usuario_id?: string | null;
  template_id: string;
  sequencial_numero?: number | null;
  sequencial_codigo?: string | null;
  snapshot_id?: string | null;
}

// Interface disponibilizada para a UI já com metadados enriquecidos
export interface ChecklistListItem extends RawChecklistItem {
  templateName: string;
  userName: string;
}

async function enrichChecklistItems(rawItems: RawChecklistItem[]): Promise<ChecklistListItem[]> {
  const templateIds = Array.from(
    new Set(rawItems.map((item) => item.template_id).filter(Boolean))
  );
  const userIds = Array.from(
    new Set(rawItems.map((item) => item.usuario_id).filter((id): id is string => Boolean(id)))
  );

  const missingTemplateIds = templateIds.filter((id) => !templateNameCache[id]);
  const missingUserIds = userIds.filter((id) => !userNameCache[id]);

  await Promise.all([
    Promise.all(
      missingTemplateIds.map(async (templateId) => {
        try {
          const template = await databaseService.readItem<any>(
            APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME,
            templateId
          );
          templateNameCache[templateId] = template?.nome?.trim() || 'Template não identificado';
        } catch (templateError) {
          console.warn('[CHECKLIST_HOOK] Falha ao carregar nome do template', templateId, templateError);
          templateNameCache[templateId] = 'Template não identificado';
        }
      })
    ),
    Promise.all(
      missingUserIds.map(async (userId) => {
        try {
          const user = await databaseService.readItem<any>(
            APP_CONSTANTS.DATABASE.TABLES.USERS.NAME,
            userId
          );
          userNameCache[userId] = user?.name?.trim()
            || user?.email?.trim()
            || 'Usuário não identificado';
        } catch (userError) {
          console.warn('[CHECKLIST_HOOK] Falha ao carregar nome do usuário', userId, userError);
          userNameCache[userId] = 'Usuário não identificado';
        }
      })
    ),
  ]);

  return rawItems.map((item) => ({
    ...item,
    templateName: templateNameCache[item.template_id] || 'Template não identificado',
    userName: item.usuario_id
      ? userNameCache[item.usuario_id] || 'Usuário não identificado'
      : 'Usuário não identificado',
  }));
}

async function fetchChecklistPage({
  empresaId,
  usuarioId,
  status,
  page,
  limit = PAGE_SIZE,
}: {
  empresaId: string;
  usuarioId?: string | null;
  status: ChecklistStatus;
  page: number;
  limit?: number;
}): Promise<ChecklistListItem[]> {
  const query: Record<string, any> = {
    empresa_id: empresaId,
    status,
    limit,
    offset: page * limit,
    orderBy: 'data_criacao DESC', // Mantém ordenação por criação (mais recente primeiro) com campo validado
  };

  if (usuarioId) {
    query.usuario_id = usuarioId;
  }

  const result = await databaseService.listItems(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLISTS.NAME, query);

  const rawItems = (result as RawChecklistItem[]).filter(item => !(item as any).retired_at);
  const readyItems = await filterChecklistsWithReadySnapshots(rawItems);
  return enrichChecklistItems(readyItems);
}

// Estado global leve apenas para metadados da UI, não para os dados dos checklists
export const listPaging = observable({
  page: 0,
  filtro: 'in_progress' as 'in_progress' | 'done'
});

/**
 * Hook para gerenciar listagem paginada de checklists
 * NÃO coloca arrayCompletoDeChecklists no Legend State - dados ficam no DB
 * Versão final com cleanup de debounce e função retry
 * @param empresaId ID da empresa para filtrar os checklists
 * @returns {data, error, loading, retry} com array de checklists, possível erro, flag de loading e função retry
 */
export function useChecklistsList(empresaId: string, options?: { usuarioId?: string | null }) {
  const [data, setData] = useState<ChecklistListItem[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const usuarioId = options?.usuarioId || null;
  const realtimeCounter = realtimeTrigger.counter.get();

  const { beginRead, identity, coherent } = useChecklistReadGuard(JSON.stringify([
    empresaId, usuarioId, listPaging.page.get(), listPaging.filtro.get(), realtimeCounter,
  ]), empresaId);

  const displayScope = JSON.stringify([identity, empresaId, usuarioId]);
  const previousDisplayScope = useRef(displayScope);

  // ✅ useRef para controlar timeout do debounce
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastRealtimeCounterRef = useRef<number | null>(null);

  /**
   * Função utilitária de debounce com cleanup
   */
  const debounce = useCallback(<T extends (...args: any[]) => any>(func: T, delay: number): T => {
    return ((...args: any[]) => {
      // Limpar timeout anterior
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Agendar nova execução
      timeoutRef.current = setTimeout(() => func(...args), delay);
    }) as T;
  }, []);

  // Função de fetch dos dados
  const fetchChecklists = useCallback(async (empresaId: string, filtro: string, page: number, checklistUsuarioId?: string | null) => {
    const isCurrent = beginRead();
    if (!isCurrent()) return;
    try {
      setLoading(true);
      setError(null); // ✅ Limpar error antes de cada fetch

      // Validação: se empresaId estiver vazio, retornar array vazio
      if (!empresaId || empresaId.trim() === '') {
        console.info('[CHECKLIST_HOOK] empresaId vazio, retornando lista vazia');
        setData([]);
        setLoading(false);
        return;
      }

      console.info(`[CHECKLIST_HOOK] Carregando página ${page}, filtro: ${filtro}`);

      // Consulta paginada no banco local
      const enrichedItems = await fetchChecklistPage({
        empresaId,
        usuarioId: checklistUsuarioId,
        status: filtro as ChecklistStatus,
        page,
        limit: PAGE_SIZE,
      });

      if (!isCurrent()) return;
      setData(enrichedItems);
      console.info(`[CHECKLIST_HOOK] Carregados ${enrichedItems.length} checklists`);
    } catch (err) {
      if (!isCurrent()) return;
      console.error('[CHECKLIST_HOOK] Erro ao carregar checklists:', err);
      setError(err as Error);
      // Em caso de erro, não limpar dados existentes para UX melhor
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [beginRead]);

  // ✅ Debounce da função de fetch (100ms)
  const debouncedFetch = useCallback(
    debounce(fetchChecklists, 100),
    [fetchChecklists, debounce]
  );

  // ✅ Função retry para reexecutar fetch manualmente
  const retry = useCallback(() => {
    console.info('[CHECKLIST_HOOK] Executando retry manual');
    fetchChecklists(empresaId, listPaging.filtro.get(), listPaging.page.get(), usuarioId);
  }, [fetchChecklists, empresaId, usuarioId]);

  useEffect(() => {
    if (lastRealtimeCounterRef.current !== null && lastRealtimeCounterRef.current !== realtimeCounter) {
      clearChecklistMetadataCaches();
      console.info('[CHECKLIST_HOOK] Cache de metadados invalidado após atualização realtime/sync');
    }
    lastRealtimeCounterRef.current = realtimeCounter;

    if (previousDisplayScope.current !== displayScope) {
      setData([]);
      previousDisplayScope.current = displayScope;
    }
    setError(null);
    setLoading(true);
    debouncedFetch(empresaId, listPaging.filtro.get(), listPaging.page.get(), usuarioId);

    // ✅ Cleanup: abortar debounce pendente em unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        console.info('[CHECKLIST_HOOK] Debounce abortado no unmount');
      }
    };
  }, [empresaId, usuarioId, listPaging.page.get(), listPaging.filtro.get(), debouncedFetch, realtimeCounter, displayScope]);

  return { data: coherent && previousDisplayScope.current === displayScope ? data : [], error, loading, retry };
}

/**
 * Função auxiliar para incrementar a página (chamada no onEndReached)
 */
export const loadNextPage = () => {
  listPaging.page.set((currentPage) => currentPage + 1);
  console.info(`[CHECKLIST_HOOK] Carregando próxima página: ${listPaging.page.get()}`);
};

/**
 * Função auxiliar para resetar a paginação
 */
export const resetPagination = () => {
  listPaging.page.set(0);
  console.info('[CHECKLIST_HOOK] Paginação resetada');
};

/**
 * Função auxiliar para alterar o filtro e resetar a paginação
 */
export const changeFilter = (newFilter: 'in_progress' | 'done') => {
  listPaging.filtro.set(newFilter);
  listPaging.page.set(0); // Reset da página ao trocar filtro
  console.info(`[CHECKLIST_HOOK] Filtro alterado para: ${newFilter}, página resetada`);
};

/**
 * Hook auxiliar para carregar listas paralelas de checklists (andamento x concluídos)
 * Usado em layouts amplos (landscape) para exibir colunas simultâneas.
 */
export function useChecklistsSplitList(
  empresaId: string,
  options?: { enabled?: boolean; usuarioId?: string | null }
) {
  const enabled = options?.enabled ?? true;
  const usuarioId = options?.usuarioId || null;
  const realtimeCounter = realtimeTrigger.counter.get();
  const [data, setData] = useState<{ inProgress: ChecklistListItem[]; done: ChecklistListItem[] }>({
    inProgress: [],
    done: [],
  });
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(enabled);
  const lastRealtimeCounterRef = useRef<number | null>(null);

  const { beginRead, identity, coherent } = useChecklistReadGuard(JSON.stringify([empresaId, usuarioId, enabled, realtimeCounter]), empresaId);
  const displayScope = JSON.stringify([identity, empresaId, usuarioId]);
  const previousDisplayScope = useRef(displayScope);
  const load = useCallback(async () => {
    const isCurrent = beginRead();
    if (!isCurrent()) return;
    if (!enabled) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (!empresaId || empresaId.trim() === '') {
        console.info('[CHECKLIST_HOOK_SPLIT] empresaId vazio, retornando listas vazias');
        setData({ inProgress: [], done: [] });
        setLoading(false);
        return;
      }

      const [inProgress, done] = await Promise.all([
        fetchChecklistPage({ empresaId, usuarioId, status: 'in_progress', page: 0 }),
        fetchChecklistPage({ empresaId, usuarioId, status: 'done', page: 0 }),
      ]);

      if (!isCurrent()) return;
      setData({ inProgress, done });
      console.info(`[CHECKLIST_HOOK_SPLIT] Carregados ${inProgress.length} em andamento e ${done.length} concluídos`);
    } catch (err) {
      if (!isCurrent()) return;
      console.error('[CHECKLIST_HOOK_SPLIT] Erro ao carregar listas paralelas:', err);
      setError(err as Error);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [empresaId, enabled, usuarioId, beginRead]);

  const retry = useCallback(() => {
    if (!enabled) return;
    console.info('[CHECKLIST_HOOK_SPLIT] Retry manual solicitado');
    load();
  }, [enabled, load]);

  useEffect(() => {
    if (!enabled || previousDisplayScope.current !== displayScope) {
      setData({ inProgress: [], done: [] });
      previousDisplayScope.current = displayScope;
    }
    setError(null);
    if (!enabled) {
      setLoading(false);
      return;
    }

    if (lastRealtimeCounterRef.current !== null && lastRealtimeCounterRef.current !== realtimeCounter) {
      clearChecklistMetadataCaches();
      console.info('[CHECKLIST_HOOK_SPLIT] Cache de metadados invalidado após atualização realtime/sync');
    }
    lastRealtimeCounterRef.current = realtimeCounter;

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, realtimeCounter, enabled, displayScope]);

  return { data: coherent && previousDisplayScope.current === displayScope ? data : { inProgress: [], done: [] }, error, loading, retry };
}
